import React, { useState } from 'react';
import { Button } from 'antd';

import { BaseButton, Modal } from '@common/components';
import { createMessage } from 'works/legacy/lib/message-helper';
import { useMutation } from '@common/hooks';
import {
  InvoiceListItemFragmentType,
  PerformWorksInvoiceAction,
} from '@pay-bill/graphql';
import { Colors } from '@common/tokens';

const feedbackMap: Record<string, string> = {
  void: 'voided',
  markPaid: 'marked Paid',
};

const actionMap: Record<string, string> = {
  void: 'void',
  pay: 'pay',
};

export const InvoiceBulkActionButtons = ({
  invoices,
  onFinish,
}: {
  invoices: InvoiceListItemFragmentType[];
  onFinish?: () => void;
}) => {
  const [action, setAction] = useState<'markPaid' | 'void' | undefined>();
  const [loading, setLoading] = useState(false);
  const [confirmVisible, setConfirmVisible] = useState(false);

  const [performActionMutation] = useMutation(PerformWorksInvoiceAction, {
    refetchQueries: ['InvoiceReviewQuery'],
    onCompleted: () => {
      createMessage(
        'success',
        `Invoice${invoices.length > 1 ? 's' : null} successfully ${
          feedbackMap[action!]
        }.`
      );
      setAction(undefined);
      setLoading(false);
      setConfirmVisible(false);
      onFinish?.();
    },
    onError: () => {
      createMessage(
        'error',
        `An error occurred while trying to ${actionMap[action!]} the invoice${
          invoices.length > 1 ? 's' : null
        }`
      );
      setAction(undefined);
      setLoading(false);
      setConfirmVisible(false);
    },
  });

  const handleAction = (action: 'markPaid' | 'void') => {
    setAction(action);
    setConfirmVisible(true);
  };

  const onActionConfirm = (action: 'markPaid' | 'void' | undefined) => {
    setLoading(true);
    const castAction = action === 'markPaid' ? 'mark_paid' : 'void';

    performActionMutation({
      variables: {
        invoiceIds: invoices.map((invoice) => invoice.id),
        action: castAction,
      },
    });
  };

  const canVoid = invoices.every((it) => it.canVoid.value);
  const canMarkPaid = invoices.every((it) => it.canMarkPaid.value);

  return (
    <>
      {canVoid && (
        <Button
          type="ghost"
          className="invoice-bulk-action-void"
          loading={loading}
          style={{ color: Colors.coral, marginRight: 10 }}
          onClick={() => handleAction('void')}
        >
          Void
        </Button>
      )}
      {canMarkPaid && (
        <Button
          type="primary"
          className="invoice-bulk-action-mark-paid"
          loading={loading}
          onClick={() => handleAction('markPaid')}
        >
          Mark Paid
        </Button>
      )}
      <Modal
        visible={confirmVisible}
        title={`Are you sure you want to ${
          action === 'markPaid'
            ? invoices.length > 1
              ? 'mark all these invoices paid'
              : 'mark this invoice paid'
            : invoices.length > 1
              ? 'void all these invoices'
              : 'void this invoice'
        }?`}
        onCancel={() => setConfirmVisible(false)}
        footer={
          <>
            <BaseButton
              type="ghost"
              className="invoice-bulk-action-cancel color-elm"
              onClick={() => setConfirmVisible(false)}
              disabled={loading}
            >
              No, Cancel
            </BaseButton>
            <BaseButton
              type="primary"
              className="invoice-bulk-action-confirm"
              onClick={() => onActionConfirm(action)}
              loading={loading}
            >
              Yes
            </BaseButton>
          </>
        }
      >
        This action cannot be undone.
      </Modal>
    </>
  );
};
